import React, { useMemo, useRef, useState } from "react";
import moment from "moment";
import { t } from "i18next";

import "./styles.css";
import { useClickOutside } from "hooks";
import { Icon, RangeCalendar } from "components";

const containsDateRange = (value) => {
  const dateRangeRegex = /\b([A-Za-z]{3} \d{1,2}, \d{4}) - ([A-Za-z]{3} \d{1,2}, \d{4})\b/;

  return dateRangeRegex.test(value);
};

export default function MoveInPicker({ value, options, onSelect, selectorType, filterData }) {
  const pickerRef = useRef(null);

  const [isOpened, setIsOpened] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selected, setSelected] = useState("any");
  const [custom, setCustom] = useState(false);

  const isDisabled = selectorType === "All";

  useClickOutside(pickerRef, () => {
    onCalendarHide();
  });

  const onDropdownClick = () => {
    if (isDisabled) {
      return;
    }

    if (isCalendarOpen) {
      onCalendarHide();
    } else {
      setIsOpened((prev) => !prev);
    }
  };

  const onItemClick = (option) => {
    setSelected(option.value);
    setIsOpened(false);

    switch (option.value) {
      case "any":
        onSelect("any", "moveIns");
        break;
      case "custom":
        setCustom(true);
        setIsCalendarOpen(true);
        break;
      case "asap":
        onSelect(moment().add(7, "days").format("YYYY-MM-DD"), "moveIns", true);
        break;
      case "next_30_days":
        onSelect(moment().add(30, "days").format("YYYY-MM-DD"), "moveIns");
        break;
      case "next_60_days":
        onSelect(moment().add(60, "days").format("YYYY-MM-DD"), "moveIns");
        break;
      default:
        break;
    }
  };

  const onCalendarHide = (start, end) => {
    if (start && end) {
      onSelect(`${start.format("YYYY-MM-DD")},${end.format("YYYY-MM-DD")}`, "moveIns");
    }

    setIsCalendarOpen(false);
    setIsOpened(false);
    setCustom(false);
  };

  const getSelectedLabel = useMemo(() => {
    let vals = filterData?.moveIns || "any";

    setSelected(value);

    const item = options.find((option) => option.date === vals) || options.find((option) => option.value === vals);

    if (custom) {
      return t("custom");
    }

    if (!item) {
      if (value) {
        const split = value.split(",");

        if (split.length > 1) {
          return `${moment(split[0]).format("MMM DD, YYYY")} - ${moment(split[1]).format("MMM DD, YYYY")}`;
        }

        return `${moment().format("MMM DD, YYYY")} - ${moment(split[0]).format("MMM DD, YYYY")}`;
      }

      return t("custom");
    }

    return t(item.trans);
  }, [filterData, options, selected, value, custom]);

  return (
    <div className="move-in-picker-div" ref={pickerRef} disabled={true}>
      <div
        className={`move-in-picker-button ${isDisabled ? " hayley-cursor-default hayley-opacity-30" : " hayley-text-secondary hayley-opacity-75"}`}
        onClick={onDropdownClick}
      >
        <span className="move-in-picker-text">
          {t("moveIn")} ({t(getSelectedLabel)})
        </span>
        <Icon iconType={isOpened || isCalendarOpen ? "up" : "down"} className="move-in-picker-icon" />
      </div>
      {isOpened ? (
        <div className="move-in-picker-dropdown">
          {options.map((option, i) => (
            <div
              key={i}
              onClick={() => onItemClick(option)}
              className={`move-in-picker-dropdown-item hayley-p-2 hover:hayley-bg-gray-100 ${
                (filterData?.moveIns
                  ? filterData?.moveIns === "any"
                    ? option.value === filterData["moveIns"]
                    : option.date === filterData["moveIns"]
                  : selected === option.value) ||
                option.label === getSelectedLabel ||
                (option.label === "Custom Range" && containsDateRange(getSelectedLabel))
                  ? "move-in-selected-item"
                  : ""
              }`}
            >
              {t(option.trans)}
            </div>
          ))}
        </div>
      ) : null}
      {isCalendarOpen ? <RangeCalendar onHide={onCalendarHide} /> : null}
    </div>
  );
}
